import { FC, ReactNode, Suspense } from "react";
import Loader from "components/atoms/loader/Loader";
import { Navigate, RouteObject } from "react-router-dom";
import nav from "constants/nav";
import { AccessRoute, hasAccess } from "utils/helper/access";

export const createLazyComponent = (Component, props = {}) => (
  <Suspense fallback={<Loader spinner />}>
    <Component {...props} />
  </Suspense>
);

export const createRoute = (path, Component, props = {}) => ({
  path,
  element: createLazyComponent(Component, props),
});

export interface RouteDef extends AccessRoute {
  namespace?: string;
  component?: FC;
  element?: ReactNode;
  path: string;
  children?: RouteDef[];
  props?: any;
}

export const createRoutes = (routes: RouteDef[], abilities, role: string) => {
  const result: RouteObject[] = [];

  for (const route of routes) {
    if (hasAccess(route, abilities, role)) {
      const element = route.component ? createLazyComponent(route.component, route.props) : route.element;
      const path = route.path;

      if (route.children) {
        const children = createRoutes(route.children, abilities, role);

        if (children?.length) {
          const firstPermittedPath = children[0].path;
          const indexRoute: RouteObject[] = path && path !== firstPermittedPath ? [{
            index: true,
            element: (
              <Navigate
                replace={true}
                to={firstPermittedPath}
              />
            ),
          }] : [];

          result.push({
            element,
            path,
            children: indexRoute.concat(children),
          });
        }
      } else {
        result.push({
          element,
          path,
        });
      }
    }
  }

  return result;
};

interface NavItem extends AccessRoute {
  path: string;
  subNav?: NavItem[];
}

export function findAccessiblePath(abilities = {}, role: string, items: NavItem[] = nav) {
  if (!Object.keys(abilities).length) {
    return;
  }

  for (const item of items) {
    if (hasAccess(item, abilities, role)) {
      return item.path;
    }
    if (item.subNav) {
      const subPath = findAccessiblePath(abilities, role, item.subNav);
      if (subPath) {
        return subPath;
      }
    }
  }
}

